<template>
  <div class="d-flex flex-column justify-center ma-0 pa-0">
    <!-- file -->
    <input
      ref="file"
      :accept="accept"
      :disabled="disabled"
      hidden
      :multiple="multiple"
      type="file"
      @change="fileChange"
    />
    <input
      v-if="allowDirectory"
      ref="folder"
      :disabled="disabled"
      directory
      hidden
      multiple
      type="file"
      webkitdirectory
      @change="fileChange"
    />
    <div
      v-if="isCanChooseFile"
      class="file__upload text-center full-width py-4"
      :class="[drag ? 'drag' : '']"
      @click="goAddFile"
      @dragenter="
        if (disabled) return
        overrideDefault($event)
        hoverDrag()
      "
      @dragleave="
        if (disabled) return
        overrideDefault($event)
        hoverDragEnd()
      "
      @dragover="
        if (disabled) return
        overrideDefault($event)
        hoverDrag()
      "
      @drop="onDrop"
    >
      <v-icon size="30">mdi-paperclip</v-icon>
      <div class="mt-2">{{ title }}</div>
      <div v-if="allowDirectory" class="text-caption grey--text mt-1 px-2">
        Chọn file, chọn thư mục, hoặc kéo thả vào đây. Chỉ lấy file phù hợp.
      </div>
      <div
        v-if="allowDirectory"
        class="mt-2"
        @click.stop
      >
        <span class="folder-link" @click="goAddFolder">Chọn thư mục</span>
      </div>
    </div>
    <!-- end file -->
    <div v-if="folderHint" class="text-caption error--text mt-2">
      {{ folderHint }}
    </div>
    <template v-if="currentFiles">
      <div class="list--item--file">
        <!-- file -->
        <div
          v-for="(item, index) in currentFiles"
          :key="(item && item.name) + '-' + index"
          class="mt-2 file-row"
          :class="{
            'file-row--error': isFileError(item),
            'file-row--warn': isFileWarn(item),
          }"
        >
          <div class="d-flex align-center">
            <div
              class="d-flex align-center full-width flex-grow-1 overflow-hidden"
              :class="{ 'file-row__toggle': showFileExtra(item) }"
              @click="toggleFileExtra(item, index)"
            >
              <div class="flex-grow-0">
                <v-icon :color="isFileError(item) ? 'error' : undefined">
                  {{
                    isFileError(item)
                      ? 'mdi-file-alert-outline'
                      : 'mdi-file-outline'
                  }}
                </v-icon>
              </div>
              <div class="flex-grow-1 overflow-hidden ml-1">
                <div
                  class="texttruncate"
                  :class="{
                    'error--text font-weight-medium': isFileError(item),
                  }"
                  :title="displayName(item)"
                >
                  {{ displayName(item) }}
                </div>
                <div
                  v-if="fileStateLabel(item)"
                  class="text-caption"
                  :class="
                    isFileError(item)
                      ? 'error--text'
                      : 'grey--text text--darken-1'
                  "
                >
                  {{ fileStateLabel(item) }}
                </div>
              </div>
            </div>
            <v-icon
              v-if="showFileExtra(item)"
              class="file-row__toggle flex-grow-0"
              :color="isFileError(item) ? 'error' : undefined"
              @click="toggleFileExtra(item, index)"
            >
              {{
                isFileExtraOpen(item, index)
                  ? 'mdi-chevron-up'
                  : 'mdi-chevron-down'
              }}
            </v-icon>
            <base-button
              class="ml-1 flex-grow-0"
              :disabled="disabled"
              icon
              style="cursor:pointer"
              @click="removeFile(index)"
            >
              <v-icon :color="isFileError(item) ? 'error' : undefined">
                mdi-close
              </v-icon>
            </base-button>
          </div>
          <v-expand-transition>
            <div
              v-show="showFileExtra(item) && isFileExtraOpen(item, index)"
              class="file-row__extra"
              @click.stop
            >
              <slot name="file-extra" :item="item" :index="index" />
            </div>
          </v-expand-transition>
        </div>
        <!-- end file -->
      </div>
    </template>
    <VMessages class="pt-2" color="error" :value="errorMessages" />
  </div>
</template>
<script>
import VInput from 'vuetify/lib/components/VInput/VInput.js'
export default {
  extends: VInput,
  props: {
    value: {
      required: false,
    },
    title: {
      required: true,
      type: String,
    },
    multiple: Boolean,
    allowDirectory: Boolean,
    accept: { type: String, default: 'image/*, video/*, application/pdf' },
    errorMessages: { type: [String, Array], default: () => [] },
    fileState: { type: Function, default: null },
  },
  data: () => ({
    files: [],
    drag: false,
    folderHint: '',
    extraOpen: {},
  }),
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.files = value
        } else {
          this.files = null
        }
      },
    },
  },
  created() {
    this.$root.$on('rr-reset-input', () => {
      this.reset()
    })
  },
  computed: {
    isCanChooseFile() {
      return !(!this.multiple && this.currentFiles.length == 1)
    },
    currentFiles() {
      if (this.multiple) return this.files || []
      else if (this.files) return [this.files]
      return []
    },
    acceptTokens() {
      return String(this.accept || '')
        .split(',')
        .map((token) => token.trim().toLowerCase())
        .filter(Boolean)
    },
  },
  methods: {
    // inputfile
    //drag event
    hoverDrag() {
      this.drag = true
    },
    hoverDragEnd() {
      this.drag = false
    },
    overrideDefault(e) {
      e.preventDefault()
      e.stopPropagation()
    },
    goAddFile() {
      if (this.disabled) return
      this.$refs.file.click()
    },
    goAddFolder() {
      if (this.disabled || !this.$refs.folder) return
      this.$refs.folder.click()
    },
    fileChange(e) {
      const list = e.target && e.target.files
      this.appendFiles(this.toFileArray(list))
      if (e.target) e.target.value = ''
    },
    async onDrop(e) {
      if (this.disabled) return
      this.overrideDefault(e)
      this.hoverDragEnd()
      const files = await this.collectDroppedFiles(e)
      this.appendFiles(files)
    },
    toFileArray(list) {
      if (!list) return []
      return Array.prototype.slice.call(list)
    },
    appendFiles(incoming) {
      const picked = this.toFileArray(incoming)
      const accepted = picked.filter((file) => this.isAcceptedFile(file))
      if (!accepted.length) {
        this.folderHint = picked.length
          ? 'Không tìm thấy file phù hợp trong lựa chọn'
          : ''
        return
      }
      this.folderHint = ''
      if (this.multiple) {
        const current = this.files ? [...this.files] : []
        const seen = {}
        current.forEach((file) => {
          seen[this.fileIdentity(file)] = true
        })
        accepted.forEach((file) => {
          const key = this.fileIdentity(file)
          if (seen[key]) return
          seen[key] = true
          current.push(file)
        })
        this.files = current
      } else {
        this.files = accepted[0]
      }
      this.$emit('input', this.files)
    },
    fileIdentity(file) {
      return [
        file && file.webkitRelativePath,
        file && file.name,
        file && file.size,
        file && file.lastModified,
      ].join(':')
    },
    displayName(item) {
      return (item && item.webkitRelativePath) || (item && item.name) || ''
    },
    isIgnoredFile(file) {
      const name = (file && file.name) || ''
      if (!name || name.charAt(0) === '.') return true
      const lower = name.toLowerCase()
      if (lower === 'thumbs.db' || lower === 'desktop.ini') return true
      const rel = String((file && file.webkitRelativePath) || '')
      return rel.indexOf('__MACOSX') !== -1
    },
    isAcceptedFile(file) {
      if (!file || this.isIgnoredFile(file)) return false
      const tokens = this.acceptTokens
      if (!tokens.length) return true
      const name = String(file.name || '').toLowerCase()
      const type = String(file.type || '').toLowerCase()
      return tokens.some((token) => {
        if (token.charAt(0) === '.') return name.endsWith(token)
        if (token.slice(-2) === '/*') {
          return type.indexOf(token.slice(0, -1)) === 0
        }
        return type === token || name.endsWith('.' + token)
      })
    },
    async collectDroppedFiles(e) {
      const items = e.dataTransfer && e.dataTransfer.items
      if (this.allowDirectory && items && items.length) {
        const tasks = []
        for (let i = 0; i < items.length; i += 1) {
          const item = items[i]
          const entry =
            item && item.webkitGetAsEntry && item.webkitGetAsEntry()
          if (entry) {
            tasks.push(this.readEntry(entry))
          }
        }
        if (tasks.length) {
          const nested = await Promise.all(tasks)
          return nested.reduce((out, files) => out.concat(files), [])
        }
      }
      return this.toFileArray(e.dataTransfer && e.dataTransfer.files)
    },
    readEntry(entry) {
      if (!entry) return Promise.resolve([])
      if (entry.isFile) {
        return new Promise((resolve) => {
          entry.file(
            (file) => resolve([file]),
            () => resolve([])
          )
        })
      }
      if (!entry.isDirectory) return Promise.resolve([])
      return this.readDirectory(entry)
    },
    readDirectory(dirEntry) {
      const reader = dirEntry.createReader()
      const readAll = (acc) =>
        new Promise((resolve) => {
          reader.readEntries(
            (entries) => {
              if (!entries.length) {
                resolve(acc)
                return
              }
              Promise.all(entries.map((child) => this.readEntry(child))).then(
                (nested) => {
                  const next = nested.reduce(
                    (out, files) => out.concat(files),
                    acc
                  )
                  readAll(next).then(resolve)
                },
                () => resolve(acc)
              )
            },
            () => resolve(acc)
          )
        })
      return readAll([])
    },
    async downloadFile(item) {
      this.$emit('click:download', item)
    },
    reset() {
      this.files = []
      this.folderHint = ''
      this.$emit('input', this.files)
    },
    removeFile(index) {
      if (this.multiple) {
        this.files.splice(index, 1)
      } else {
        this.files = null
      }
      this.$emit('input', this.files)
    },
    fileStateOf(item) {
      if (typeof this.fileState !== 'function' || !item) return null
      return this.fileState(item) || null
    },
    isFileError(item) {
      const state = this.fileStateOf(item)
      return !!(state && state.error)
    },
    isFileWarn(item) {
      const state = this.fileStateOf(item)
      return !!(state && state.georef && !state.error)
    },
    showFileExtra(item) {
      const hasSlot = !!(this.$scopedSlots && this.$scopedSlots['file-extra'])
      const state = this.fileStateOf(item)
      return hasSlot && !!(state && state.georef)
    },
    extraKey(item, index) {
      return this.fileIdentity(item) || String(index)
    },
    isFileExtraOpen(item, index) {
      const key = this.extraKey(item, index)
      if (Object.prototype.hasOwnProperty.call(this.extraOpen, key)) {
        return this.extraOpen[key]
      }
      return this.showFileExtra(item)
    },
    toggleFileExtra(item, index) {
      if (!this.showFileExtra(item)) return
      const key = this.extraKey(item, index)
      this.$set(this.extraOpen, key, !this.isFileExtraOpen(item, index))
    },
    fileStateLabel(item) {
      const state = this.fileStateOf(item)
      return (state && state.label) || ''
    },
  },
}
</script>
<style scoped>
/* effect drag */
.drag {
  border: 3px dashed var(--v-colorText-base);
}
.file__upload {
  background-color: #eceff1;
  border-radius: 6px;
  cursor: pointer;
}
.v-icon.mdi-paperclip {
  transform: rotate(45deg);
}
.ls_document {
  color: var(--v-colorText-base);
}
.file-row {
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
}
.file-row--error {
  background: #ffebee;
  border-color: #ef9a9a;
}
.file-row--warn {
  background: #fff8e1;
  border-color: #ffe082;
}
.file-row__toggle {
  cursor: pointer;
  user-select: none;
}
.file-row__extra {
  padding: 8px 4px 4px;
}
.folder-link {
  color: var(--v-primary-base, #1976d2);
  cursor: pointer;
  text-decoration: underline;
}
@media (max-width: 600px) {
  .file__upload {
    background-color: #eceff1;
    padding-left: 50px !important;
    padding-right: 50px !important;
    border-radius: 6px;
    cursor: pointer;
  }
}
</style>
